import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Hero.css';

const Hero = () => {
  const [isPlaying] = useState(true);
  const [currentScene, setCurrentScene] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const scenes = [
    {
      title: "ENTERPRISE",
      subtitle: "AUTOMATION",
      service: "ASMA",
      description: "Revolutionary AI-driven automation solutions that transform how enterprises operate in the digital age",
      link: "https://asma.astral-ai.io",
      color: "#d3c5aa",
      theme: "tech"
    },
    {
      title: "MYSTICAL",
      subtitle: "WISDOM",
      service: "GAIA", 
      description: "Ancient tarot wisdom reimagined through cutting-edge technology for modern spiritual guidance",
      link: "https://gaia.astral-ai.io",
      color: "#c4b593",
      theme: "mystic"
    }
  ];

  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setCurrentScene((prev) => (prev + 1) % scenes.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [isPlaying, scenes.length]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const textVariants = {
    hidden: { y: 100, opacity: 0 },
    visible: (i) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.8,
        ease: [0.6, 0.01, 0.05, 0.95]
      }
    })
  };

  return (
    <section className="hero-cinematic">
      {/* Animated Background Grid */}
      <div className="grid-overlay">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="grid-line"
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ delay: i * 0.05, duration: 1 }}
          />
        ))}
      </div>

      {/* Dynamic Background Elements */}
      <div className="dynamic-bg">
        <motion.div 
          className="bg-orb orb-1"
          animate={{
            x: mousePosition.x * 0.5,
            y: mousePosition.y * 0.3,
            scale: [1, 1.2, 1],
          }}
          transition={{ 
            x: { type: "spring", stiffness: 50 },
            y: { type: "spring", stiffness: 50 },
            scale: { duration: 4, repeat: Infinity }
          }}
        />
        <motion.div 
          className="bg-orb orb-2"
          animate={{
            x: mousePosition.x * -0.3,
            y: mousePosition.y * -0.4,
            scale: [1.2, 1, 1.2],
          }}
          transition={{ 
            x: { type: "spring", stiffness: 30 },
            y: { type: "spring", stiffness: 30 },
            scale: { duration: 6, repeat: Infinity }
          }}
        />
      </div>

      {/* Main Content */}
      <div className="hero-content-cinematic">
        {/* Header Controls */}
        <motion.div 
          className="hero-header"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <div className="logo-section">
            <img src="/logo.png" alt="Astral AI" className="logo-cinematic" />
            <span className="brand-name">ASTRAL AI</span>
          </div>
        </motion.div>

        {/* Scene Content */}
        <div className="scene-container">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentScene}
              className="scene-content"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.1 }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
            >
              <motion.div 
                className="scene-number"
                custom={0}
                variants={textVariants}
                initial="hidden"
                animate="visible"
              >
                {String(currentScene + 1).padStart(2, '0')}
              </motion.div>
              
              <motion.div 
                className="service-badge"
                custom={1}
                variants={textVariants}
                initial="hidden"
                animate="visible"
                style={{ borderColor: scenes[currentScene].color }}
              >
                {scenes[currentScene].service}
              </motion.div>
              
              <motion.h1 
                className="cinematic-title"
                custom={2}
                variants={textVariants}
                initial="hidden"
                animate="visible"
              >
                <span className="title-word">{scenes[currentScene].title}</span>
                <span 
                  className="title-word accent-word"
                  style={{ color: scenes[currentScene].color }}
                >
                  {scenes[currentScene].subtitle}
                </span>
              </motion.h1>
              
              <motion.p 
                className="scene-description"
                custom={3}
                variants={textVariants}
                initial="hidden"
                animate="visible"
              >
                {scenes[currentScene].description}
              </motion.p>
              
              <motion.div 
                className="cta-section"
                custom={4}
                variants={textVariants}
                initial="hidden"
                animate="visible"
              >
                <a 
                  href={scenes[currentScene].link} 
                  className="cta-cinematic"
                  style={{ borderColor: scenes[currentScene].color }}
                >
                  <span>EXPERIENCE {scenes[currentScene].service}</span>
                  <div className="cta-glow" style={{ backgroundColor: scenes[currentScene].color }}></div>
                </a>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Scene Indicators */}
        <motion.div 
          className="scene-indicators"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
        >
          {scenes.map((_, index) => (
            <button
              key={index}
              className={`scene-indicator ${index === currentScene ? 'active' : ''}`}
              onClick={() => setCurrentScene(index)}
            >
              <div className="indicator-fill" style={{ 
                backgroundColor: index === currentScene ? scenes[currentScene].color : 'transparent' 
              }} />
            </button>
          ))}
        </motion.div>

        {/* Progress Timeline */}
        <div className="progress-timeline">
          <motion.div 
            className="progress-fill"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: isPlaying ? 1 : 0 }}
            transition={{ 
              duration: 6,
              ease: "linear",
              repeat: isPlaying ? Infinity : 0
            }}
            style={{ backgroundColor: scenes[currentScene].color }}
          />
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="scroll-prompt"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
      >
        <span>SCROLL TO EXPLORE</span>
        <motion.div 
          className="scroll-line"
          animate={{ height: [20, 40, 20] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </motion.div>
    </section>
  );
};

export default Hero; 